<template>
    <app-layout title="Recurring Orders">
        <page-header>
            <template #title>Recurring Orders</template>

            <template #actions>
                <jet-button type="button" :href="route('recurring_orders.create')">
                    Add Recurring Order
                </jet-button>
            </template>
        </page-header>

        <div class="px-4">
            <!-- Filters -->
            <form v-if="isAdmin" class="flex flex-wrap gap-2 mb-4" @submit.prevent="handleSubmit">
                <div class="flex flex-col min-w-48">
                    <jet-label for="customer" value="Customer" />
                    <multiselect
                        v-model="form.customer_id"
                        :options="customerOptions"
                        valueProp="id"
                        trackBy="name"
                        label="name"
                        placeholder="All"
                        searchable
                        :canClear="true"
                        :canDeselect="true"
                        @change="handleSubmit"
                        class="h-7 multi-sm text-base"
                    />
                </div>

                <!-- Search -->
                <div class="flex flex-col flex-1 min-w-40">
                    <jet-label for="search" value="Search" />
                    <jet-input
                        type="text"
                        class="h-7 text-base"
                        name="search"
                        placeholder="Search..."
                        v-model="form.search"
                        @input="handleSubmit"
                    />
                </div>

                <div class="self-end">
                    <jet-tertiary-button type="button" class="h-7" @click="handleReset">
                        Reset
                    </jet-tertiary-button>
                </div>
            </form>

            <!-- Table -->
            <div class="border rounded overflow-hidden">
                <table class="table table-with-links w-full">
                    <thead>
                        <tr>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="id" :direction="form.sort_direction" :column="form.sort_column">#</sortable-header>
                            <sortable-header v-if="isAdmin" class="text-left" @sort="handleSort" sort-by="customer" :direction="form.sort_direction" :column="form.sort_column">Customer</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="schedule_frequency" :direction="form.sort_direction" :column="form.sort_column">Frequency</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="weeks_in_advance" :direction="form.sort_direction" :column="form.sort_column">Weeks in Advance</sortable-header>
                            <th class="text-left">Shipping</th>
                            <th class="text-left">Order Days</th>
                            <th class="text-left">Next Order Generation Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="order in recurringOrders.data" :key="order.id">
                            <td class="text-left">
                                <Link class="block px-1 md:px-2 py-1" :href="route('recurring_orders.show', order.id)">
                                    {{ order.id }}
                                </Link>
                            </td>
                            <td v-if="isAdmin" class="text-left">
                                <Link class="block px-1 md:px-2 py-1" :href="route('recurring_orders.show', order.id)">
                                    {{ order.customer?.name }}
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-1 md:px-2 py-1" :href="route('recurring_orders.show', order.id)">
                                    Every {{ order.schedule_frequency }} {{ order.schedule_units.slice(0, -1) }}<span v-if="order.schedule_frequency > 1">s</span>
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-1 md:px-2 py-1" :href="route('recurring_orders.show', order.id)">
                                    {{ order.weeks_in_advance }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-1 md:px-2 py-1" :href="route('recurring_orders.show', order.id)">
                                    {{ shippingMethodLabel(order.shipping_method) }}
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-1 md:px-2 py-1" :href="route('recurring_orders.show', order.id)">
                                    <span v-if="order.schedule_units === 'months' && order.schedule_ordinance?.length">{{ formatOrdinanceWithDays(order.schedule_ordinance, order.schedule_days) }}</span>
                                    <span v-if="order.schedule_units === 'weeks' && order.schedule_days?.length">{{ asCommaList(order.schedule_days).join(', ') }}</span>
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-1 md:px-2 py-1" :href="route('recurring_orders.show', order.id)">
                                    {{ formatDate(order.next_generation_date) }}
                                </Link>
                            </td>
                        </tr>
                        <tr v-if="recurringOrders.data.length === 0">
                            <td :colspan="emptyTableColspan" class="text-center py-4 text-gray-500">
                                No recurring orders found.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <pagination class="mt-3" :links="recurringOrders.links" />
        </div>
    </app-layout>
</template>

<script>
import { useForm, Link, usePage } from '@inertiajs/inertia-vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageHeader from '@/Layouts/partials/PageHeader.vue'
import JetButton from '@/Jetstream/Button'
import JetLabel from '@/Jetstream/Label'
import JetInput from '@/Jetstream/Input';
import JetTertiaryButton from '@/Jetstream/TertiaryButton'
import Pagination from '@/Jetstream/Pagination'
import Multiselect from '@vueform/multiselect'
import UseUserAuth from '@/Composables/UseUserAuth'
import { DateTime } from 'luxon'
import SortableHeader from '@/Shared/SortableHeader';

import '@vueform/multiselect/themes/default.css'

export default {
    props: ['recurringOrders', 'filters', 'customers', 'sort_column', 'sort_direction'],

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetLabel,
        JetTertiaryButton,
        Pagination,
        Multiselect,
        Link,
        SortableHeader,
        JetInput
    },

    setup() {
        const { isAdmin } = UseUserAuth(usePage().props.value.user)
        const activeCustomer = usePage().props.value.activeCustomer || null
        return { isAdmin, activeCustomer }
    },

    data() {
        return {
            form: useForm({
                customer_id: this.filters.customer_id || null,
                search: this.filters.search || '',
                sort_column: this.sort_column || 'customer',
                sort_direction: this.sort_direction || 'asc',
            }),
        }
    },

    computed: {
        customerOptions() {
            return this.customers.map(c => ({
                id: c.id,
                name: c.name,
            }))
        },

        emptyTableColspan() {
            return this.isAdmin ? 7 : 6
        },
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.form.get(route('recurring_orders.index'), { preserveState: true })
        }, 300),

        handleReset() {
            this.form.customer_id   = null
            this.form.search        = ''
            this.form.sort_column   = 'customer'
            this.form.sort_direction = 'asc'
            this.handleSubmit()
        },

        formatDate(date) {
            return date ? DateTime.fromISO(date).toFormat('MMM d, yyyy') : ''
        },

        shippingMethodLabel(value) {
            if (value === 'pickup') return 'Pickup'
            if (value === 'delivery') return 'Delivery'
            return ''
        },

        handleSort(sort) {
            this.form.sort_column = sort.column;
            this.form.sort_direction = sort.direction;
        },
    },

    watch: {
        'form.sort_column': {
            handler() {
                this.handleSubmit();
            },
        },
        'form.sort_direction': {
            handler() {
                this.handleSubmit();
            },
        },
    },
}
</script>

<style scoped>
.min-w-48 {
    min-width: 12rem;
}
</style>
